import { useEffect, useState, useCallback } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Divider,
  Stack,
  Chip,
} from '@mui/material';
import DateRangeSelector from '../../../components/DateRangeSelector';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';
import FilterSelectorWithApp from '../../../components/FilterSelectorWithApp';
import AdpopcornDashboard from '../../../components/pointhome/adpopcorn/AdpopcornDashboard';
import AdpopcornTotalMetricsDetailTable from '../../../components/pointhome/adpopcorn/table/AdpopcornTotalMetricsDetailTable';
import AdpopcornMediationRevenueMetricsTable from '../../../components/pointhome/adpopcorn/table/AdpopcornMediationRevenueMetricsTable';
import AdpopcornMediationDetailMetricsTable from '../../../components/pointhome/adpopcorn/table/AdpopcornMediationDetailMetricsTable';
import AdpopcornPlacementMetricsTable from '../../../components/pointhome/adpopcorn/table/AdpopcornPlacementMetricsTable';
import apiClient from '../../../utils/axiosClient';
import dayjs, { Dayjs } from 'dayjs';
import {
  AdpopcornTotalMetrics,
  AdpopcornMediationRevenueMetrics,
  AdpopcornMediationDetailMetrics,
  AdpopcornPlacementMetrics
} from '../../../types/adpopcorn.types';

export default function LuckyLotteryAdPopcornDetail() {
  const [adpopcornTotalMetrics, setAdpopcornTotalMetrics] = useState<AdpopcornTotalMetrics | null>(null);
  const [adpopcornMediationRevenueMetrics, setAdpopcornMediationRevenueMetrics] = useState<AdpopcornMediationRevenueMetrics | null>(null);
  const [adpopcornMediationDetailMetrics, setAdpopcornMediationDetailMetrics] = useState<AdpopcornMediationDetailMetrics | null>(null);
  const [adpopcornPlacementMetrics, setAdpopcornPlacementMetrics] = useState<AdpopcornPlacementMetrics | null>(null);
  const [thirdPartyId, setThirdPartyId] = useState<number>(-1);
  const [thirdPartyName, setThirdPartyName] = useState<string>('미디에이션 전체');
  const [adpopcornTotalRevenue, setAdpopcornTotalRevenue] = useState<number>(0);
  const [adpopcornPreviousTotalRevenue, setAdpopcornPreviousTotalRevenue] = useState<number>(0);
  const [adpopcornDailyStats, setAdpopcornDailyStats] = useState<{
    date: string;
    revenue: number;
    req: number;
    res: number;
    imp: number;
    clk: number;
  }[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().subtract(0, 'day'));
  const [initialized, setInitialized] = useState<boolean>(false);
  const [selectedApp, setSelectedApp] = useState<string | null>(null);
  const [appOptions, setAppOptions] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [selectedOs, setSelectedOs] = useState<string>('all');

  const fetchFilterOptions = useCallback(() => {
    if (!startDate || !endDate) return;
    apiClient
      .get(`/api/v1/stat/lucky-lottery/app/list`, {
        params: { start_date: startDate.format('YYYY-MM-DD'), end_date: endDate.format('YYYY-MM-DD') },
      })
      .then((response) => {
        setAppOptions(response.data);
        if (response.data.length > 0 && !selectedApp) {
          setSelectedApp(response.data[0]);
        }
      });
  }, [selectedApp, startDate, endDate]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);


  useEffect(() => {
    setInitialized(true);
  }, []);

  const fetchAdpopcornTotalMetrics = useCallback(() => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;
    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      ...(selectedApp ? { app: selectedApp } : {}),
      os_type: selectedOs
    };

    apiClient.get(`/api/v1/stat/adpopcorn/lucky-lottery/metrics/total`, {
      params: params
    }).then((response) => {
      setAdpopcornTotalMetrics(response.data);
      setAdpopcornTotalRevenue(response.data.totalRevenue);
      setAdpopcornPreviousTotalRevenue(response.data.previousTotalRevenue);
      setAdpopcornDailyStats(response.data.dailyStats);
      setErrorMessage('');
    }).catch(() => {
      setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }).finally(() => {
      setLoading(false);
    });

  }, [startDate, endDate, selectedApp, selectedOs]);

  const fetchAdpopcornMediationRevenueMetrics = useCallback(() => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;
    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      ...(selectedApp ? { app: selectedApp } : {}),
      os_type: selectedOs
    };

    apiClient.get(`/api/v1/stat/adpopcorn/lucky-lottery/metrics/mediation`, {
      params: params
    }).then((response) => {
      setAdpopcornMediationRevenueMetrics(response.data);
      setAdpopcornTotalRevenue(response.data.totalRevenue);
      setAdpopcornPreviousTotalRevenue(response.data.previousTotalRevenue);
      setAdpopcornDailyStats(response.data.dailyStats);
      setErrorMessage('');
    }).catch(() => {
      setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }).finally(() => {
      setLoading(false);
    });
  }, [startDate, endDate, selectedApp, selectedOs]);

  const fetchAdpopcornMediationDetailMetrics = useCallback(() => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;
    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string | number | undefined> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      ...(selectedApp ? { app: selectedApp } : {}),
      os_type: selectedOs,
      ...(thirdPartyId !== -1 ? { third_party_id: thirdPartyId } : {})
    };

    apiClient.get(`/api/v1/stat/adpopcorn/lucky-lottery/metrics/mediation/detail`, {
      params: params
    }).then((response) => {
      setAdpopcornMediationDetailMetrics(response.data);
      setErrorMessage('');
    }).catch(() => {
      setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }).finally(() => {
      setLoading(false);
    });
  }, [startDate, endDate, selectedApp, selectedOs, thirdPartyId]);

  const fetchAdpopcornPlacementMetrics = useCallback(() => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;
    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      ...(selectedApp ? { app: selectedApp } : {}),
      os_type: selectedOs
    };

    apiClient.get(`/api/v1/stat/adpopcorn/lucky-lottery/metrics/placement`, {
      params: params
    }).then((response) => {
      setAdpopcornPlacementMetrics(response.data);
      setAdpopcornTotalRevenue(response.data.totalRevenue);
      setAdpopcornPreviousTotalRevenue(response.data.previousTotalRevenue);
      setAdpopcornDailyStats(response.data.dailyStats);
      setErrorMessage('');
    }).catch(() => {
      setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }).finally(() => {
      setLoading(false);
    });
  }, [startDate, endDate, selectedApp, selectedOs]);

  useEffect(() => {
    if (initialized && startDate && endDate && selectedApp != null) {
      if (activeTab === 0) {
        fetchAdpopcornTotalMetrics();
      } else if (activeTab === 1) {
        fetchAdpopcornMediationRevenueMetrics();
      }
      else if (activeTab === 2) {
        fetchAdpopcornMediationDetailMetrics();
      }
      else if (activeTab === 3) {
        fetchAdpopcornPlacementMetrics();
      }
    }
  }, [
    fetchAdpopcornTotalMetrics,
    fetchAdpopcornMediationRevenueMetrics,
    fetchAdpopcornMediationDetailMetrics,
    fetchAdpopcornPlacementMetrics,
    initialized, startDate, endDate, selectedApp, activeTab, thirdPartyId, selectedOs
  ]);

  return (
    <Box
      sx={{
        opacity: loading ? 0.3 : 1,
        transition: 'opacity 0.3s ease',
        minHeight: '100vh'
      }}
    >
      <Box
        alignItems={'center'}
        display={'flex'}
      >
        <Box>
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onSearch={() => {
              if (startDate && endDate) {
                if (activeTab === 0) {
                  fetchAdpopcornTotalMetrics();
                } else if (activeTab === 1) {
                  fetchAdpopcornMediationRevenueMetrics();
                }
              }
            }}
          />
        </Box>
        <Box
          sx={{
            ml: 3
          }}
        >
          <FilterSelectorWithApp
            selectedApp={selectedApp}
            appOptions={appOptions}
            onAppChange={(setApp) => {
              if (!setApp && appOptions.length > 0) {
                setSelectedApp(appOptions[0]);
              } else {
                setSelectedApp(setApp);
              }
            }}
            onSearch={() => {
              if (startDate && endDate) {
                if (activeTab === 0) {
                  fetchAdpopcornTotalMetrics();
                } else if (activeTab === 1) {
                  fetchAdpopcornMediationRevenueMetrics();
                }
                else if (activeTab === 2) {
                  fetchAdpopcornMediationDetailMetrics();
                }
                else if (activeTab === 3) {
                  fetchAdpopcornPlacementMetrics();
                }
              }
            }}
            selectedOs={selectedOs}
            onOsChange={setSelectedOs}
          />
        </Box>
      </Box>

      <Box
        sx={{
          ...PAGE_CARD_SX,
          width: '100%',
          maxWidth: '100%',
        }}
      >
        {/* 앱 선택 (Chip) */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            mb: 1,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setSelectedApp(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: selectedApp === name ? 700 : 400,
                backgroundColor: selectedApp === name ? '#0044D2' : '#fff',
                color: selectedApp === name ? '#fff' : '#374151',
                border: `1px solid ${selectedApp === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': {
                  backgroundColor: selectedApp === name ? '#0033a0' : '#f3f4f6',
                },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>

        <Divider variant="fullWidth" sx={{ borderColor: '#e0e0e0', mb: 2 }} />

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              데이터 로딩 중...
            </Typography>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : (
          <>
            <AdpopcornDashboard
              dailyStats={adpopcornDailyStats}
              totalRevenue={adpopcornTotalRevenue}
              previousTotalRevenue={adpopcornPreviousTotalRevenue}
            />

            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              sx={{
                mt: 3
              }}
            >
              <Tab value={0} label="전체 현황" />
              <Tab value={1} label="미디에이션 매출" />
              <Tab value={2} label="미디에이션 상세 지표" />
              <Tab value={3} label="지면별 지표" />
            </Tabs>

            {
              activeTab === 0 && adpopcornTotalMetrics && startDate && endDate && (
                <AdpopcornTotalMetricsDetailTable
                  data={adpopcornTotalMetrics}
                  selectedApp={selectedApp}
                  startDate={startDate}
                  endDate={endDate}
                />
              )
            }
            {
              activeTab === 1 && adpopcornMediationRevenueMetrics && startDate && endDate && (
                <AdpopcornMediationRevenueMetricsTable
                  data={adpopcornMediationRevenueMetrics}
                  selectedApp={selectedApp}
                  startDate={startDate}
                  endDate={endDate}
                />
              )
            }
            {
              activeTab === 2 && adpopcornMediationDetailMetrics && startDate && endDate && (
                <AdpopcornMediationDetailMetricsTable
                  data={adpopcornMediationDetailMetrics}
                  thirdPartyId={thirdPartyId}
                  thirdPartyName={thirdPartyName}
                  selectedApp={selectedApp}
                  startDate={startDate}
                  endDate={endDate}
                  onThirdPartyIdChange={(id, name) => {
                    setThirdPartyId(id);
                    setThirdPartyName(name);
                  }}
                />
              )
            }
            {
              activeTab === 3 && adpopcornPlacementMetrics && startDate && endDate && (
                <AdpopcornPlacementMetricsTable
                  data={adpopcornPlacementMetrics}
                  selectedApp={selectedApp}
                  startDate={startDate}
                  endDate={endDate}
                />
              )
            }
          </>
        )}
      </Box>
    </Box>
  );
}
